import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  useConfirmEmailMutation,
  useGetProfileQuery,
  useSendConfirmEmailCodeMutation,
} from "@/services/api/entities/profile";
import { useModal } from "@/features/modals/lib";
import { getErrorMessage } from "@/services/errors";
import type { ApiError } from "@/services/errors/api-error";
import type { ProfileFormValues } from "../types";
import { getProfileSchema } from "../schema";
import { CONFIRM_EMAIL_CODE_LENGTH } from "../constants";

export const useProfileForm = () => {
  const { showModal, hideModal, isModalOpen } = useModal();

  const { data: profile } = useGetProfileQuery();
  const [sendConfirmEmailCode, { isLoading: isSendingConfirmEmailCode }] =
    useSendConfirmEmailCodeMutation();
  const [confirmEmail, { isLoading: isConfirmingEmail }] = useConfirmEmailMutation();

  const formApi = useForm<ProfileFormValues>({
    resolver: yupResolver(getProfileSchema()),
    mode: "onChange",
  });

  const handleConfirmEmail = useCallback(async () => {
    await sendConfirmEmailCode().unwrap();
    showModal();
  }, [sendConfirmEmailCode, showModal]);

  // TODO: Need to refactor this
  useEffect(() => {
    if (profile) {
      formApi.reset({
        firstName: profile.firstName,
        secondName: profile.secondName,
        lastName: profile.lastName,
        birthday: profile.birthday ? dayjs(profile.birthday) : undefined,
        mobilePhone: profile.mobilePhone,
        email: profile.email,
      });
    }
  }, [profile, formApi]);

  const handleConfirmEmailCode = useCallback(async () => {
    try {
      await confirmEmail({ code: formApi.watch("confirmEmailCode") ?? "" }).unwrap();
      hideModal();
    } catch (error) {
      formApi.setError("confirmEmailCode", {
        message: getErrorMessage((error as ApiError)?.code),
      });
    }
  }, [confirmEmail, formApi]);

  const handleChangeConfirmEmailCode = useCallback(() => {
    formApi.trigger("confirmEmailCode");
  }, [formApi]);

  const confirmEmailCodeDisabled = useMemo(() => {
    return formApi.watch("confirmEmailCode")?.length !== CONFIRM_EMAIL_CODE_LENGTH;
  }, [formApi.watch("confirmEmailCode")]);

  const handleCloseModal = useCallback(() => {
    hideModal();
    formApi.resetField("confirmEmailCode");
  }, [hideModal, formApi]);

  return {
    formApi,
    emailConfirmed: profile?.emailConfirmed,
    isModalOpen,
    showModal,
    hideModal,
    handleConfirmEmail,
    isSendingConfirmEmailCode,
    isConfirmingEmail,
    confirmEmailCodeDisabled,
    handleConfirmEmailCode,
    handleChangeConfirmEmailCode,
    handleCloseModal,
  };
};
